<script setup lang="ts">

import ItemV3ParameterBox from "@/components/itemV3/parameter/itemV3ParameterBox.vue";
import ItemV3RemoveGroupModal from "@/components/itemV3/parameter/itemV3RemoveGroupModal.vue";
import ItemV3StonesList from "@/components/itemV3/parameter/stones/itemV3StonesList.vue";
import ItemV3StonesGroupSingle from "@/components/itemV3/parameter/stones/itemV3StonesGroupSingle.vue";
import ItemV3StonesGroupRows from "@/components/itemV3/parameter/stones/itemV3StonesGroupRows.vue";
import ItemV3StonesGroupCrown from "@/components/itemV3/parameter/stones/itemV3StonesGroupCrown.vue";
import {computed, inject, nextTick, ref, watch} from "vue";
import {useStore} from "vuex";
import {useI18n} from "vue-i18n";
import {ParametersVariations} from "@/assets/ts/parametersVariations";
import ItemV3StonesGroupStarrySky from "@/components/itemV3/parameter/stones/itemV3StonesGroupStarrySky.vue";
import {getCurrentText} from "@/assets/ts/itemText";

const {t} = useI18n();

const emit = defineEmits(["patch"]);
const props = defineProps(["item"]);

const store = useStore();

const loadItemVariants = inject<any>("loadItemVariants");
const patchMultiple = inject<any>("patchMultiple");

const itemIdx = inject<any>("itemIdx");
const session = inject<any>("session");

const imageWidthPercent: number = 30
const loading = ref(false)

/**
 * Parameter box header images
 */

const mainMetal = computed(() => store.getters["session/getMainMetal"])
const boxIdx = "stones"

const getHeaderImage = (size: string = "600x300", withBg = false) => {
  if (withBg) {
    return `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/menu/${size}/${mainMetal.value}_${boxIdx}.webp`
  } else {
    return `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/menu/${size}/${boxIdx}.webp`
  }
}

/**
 * session
 */

const configureAll = computed(() => store.getters["session/getConfigureAll"])

// used only for its imageUrl() helper (which does not depend on variantIdx), so no
// specific variant idx is needed here
const parametersVariations = new ParametersVariations("", configureAll, session, itemIdx, loadItemVariants)

// Tracks the box's own open/closed state (bound via v-model:open below) instead of a
// hardcoded "false", so it survives re-renders triggered by a patch - see
// itemV3ParameterBox.vue's onUpdated hook, which re-asserts this value on every update.
const open = ref(false)

const currentStonesText = computed(() => {
  const text = getCurrentText(
      configureAll.value ? -1 : itemIdx.value, [
        "stones.*.stone_setting",
        "stones.*.stone_size",
        "stones.*.stone_quality"
  ], true)

  if (text.includes(", , ")) {
    return text.replace(", , ", t('components.parameters.stones.no_stones'))
  }

  return text
})

/**
 * stone layout types
 */

const stoneLayoutTypes = [
  "noStones", // no stones at all - a plain ring
  "single", // a stone group with one or more single stones
  "row", // a row of stones
  "crown", // a row of stones with the special crown setting
  "starry_sky", // Starry sky stones settings
]

/**
 * stone layout type labels / images
 *
 * Renders the "no stones" / "add stones" horizontal list below the stone group list.
 * "noStones" removes all stones and the remaining types (single/row/crown) add a new
 * stone group with that layout.
 */

const stoneLayoutLabels: Record<string, string> = {
  noStones: "Ohne Steine",
  single: "Einzelner Stein",
  row: "Steinreihe",
  crown: "Kronen-fassung",
  starry_sky: "Sternen-himmel",
}

const getStoneLayoutLabel = (layoutType: string) => stoneLayoutLabels[layoutType] || layoutType

// subtitles shown below the title of each layout card, matching the attached layout
const stoneLayoutSubtitles: Record<string, string> = {
  noStones: "Klassisch",
  single: "Dezent",
  row: "Zeitlos",
  crown: "Randlos",
  starry_sky: "Funkelnd",
}

const getStoneLayoutSubtitle = (layoutType: string) => stoneLayoutSubtitles[layoutType] || ""

const getStoneLayoutImageUrl = (layoutType: string, size: 300 | 600 = 600) => {
  return `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/stone_layout/${size}/${layoutType}.webp`
}

/**
 * group title
 *
 * The group title is related to the group's stoneLayout (e.g. "Steinreihe #1",
 * "Sternenhimmel #3") - each layout has its own text stored in de.json, falling back
 * to the generic "Stein-Gruppe #n" text when the layout has no dedicated entry.
 */

const stoneGroupNameTypes = ["single", "row", "crown", "starry_sky"]

const getStoneGroupTitle = (group: any) => {
  const key = stoneGroupNameTypes.includes(group.stone_layout) ? group.stone_layout : "default"
  return t('components.parameters.stoneGroupNames.' + key, {n: group.group_idx + 1})
}

const handleStoneLayoutClick = (layoutType: string) => {
  if (layoutType === "noStones") {
    confirmRemoveStones(`stones.remove`)
    return
  }
  // Only expect a new group to show up for the item currently displayed here - e.g. not
  // when "only ring 1" is selected and this component shows a different item.
  if (!hasMultipleItems.value || applyToBothRings.value || itemIdx.value === 0) {
    pendingNewGroupLayout.value = layoutType
  }
  handleValuePatch("stones.add", layoutType)
}

/**
 * stone group layout click (while a stone group is selected)
 */

const handleGroupLayoutClick = (layoutType: string) => {
  if (layoutType === "noStones") {
    confirmRemoveStones(`stones.${activeGroupIdx.value}.remove`)
    return
  } else if (layoutType != activeGroupLayout.value) {
    handleValuePatch(`stones.${activeGroupIdx.value}.add`, layoutType)
  }
}

/**
 * remove stones / stone group
 *
 * Shared with itemV3Grooves.vue / itemV3StonesList.vue's remove confirmation modal:
 * the actual patch (either removing all stones or just the active group) is only sent
 * once the user confirms.
 */

const showRemoveModal = ref(false)
const pendingRemoveAction = ref<string | null>(null)

const confirmRemoveStones = (actionIdx: string) => {
  pendingRemoveAction.value = actionIdx
  showRemoveModal.value = true
}

const pendingRemoveItemName = computed(() => {
  const action = pendingRemoveAction.value
  if (!action) return ''
  if (action === 'stones.remove') return 'Alle Steine'

  const match = action.match(/^stones\.(\d+)\.remove$/)
  if (match) {
    const groupIdx = parseInt(match[1])
    const group: any = stoneGroupList.value.find((g: any) => g.group_idx === groupIdx)
    if (group) return getStoneGroupTitle(group)
  }
  return ''
})

const handleRemoveStones = () => {
  if (pendingRemoveAction.value) {
    const actionIdx = pendingRemoveAction.value
    handleValuePatch(actionIdx, 'remove')
    if (actionIdx === `stones.${activeGroupIdx.value}.remove`) {
      activeGroupIdx.value = -1
    }
  }
  showRemoveModal.value = false
  pendingRemoveAction.value = null
}

/**
 * target items (to which item(s) a newly added stone group is applied)
 *
 * "Only ring 1" / "both rings" choice used when adding a new stone group (stones.add)
 * in a multi-item session.
 */

const applyToBothRings = ref(false)

const hasMultipleItems = computed(() => (session.value?.items?.length || 0) > 1)

const ringsSliderStyle = computed(() => {
  const width = 100 / 2;
  const left = (applyToBothRings.value ? 1 : 0) * width;

  return {
    width: `calc(${width}% - 4px)`,
    left: `calc(${left}% + 2px)`,
  };
})

/**
 * image
 */



const imageUrl = computed(() => parametersVariations.imageUrl([], -1))

/**
 * stone group selection
 *
 * The list of current stone groups and the "select a group" / "add"/"remove" logic
 * lives in itemV3StonesList.vue (transferred from itemV3Grooves.vue). It reports the
 * current groups back via the "groups" event, and the active group is kept here so the
 * switch-button bar and the stone group details below can both react to it.
 */

/**
 * stone groups
 *
 * Transferred from itemV3Grooves.vue's "grooves" computed: scans the current item's
 * variants for idx patterns "stones.<n>.<attr>" (ignoring the "add"/"remove" actions)
 * and groups them by <n> into one object per stone group.
 */

const stoneGroups = computed(() => {
  const groups: any = {}

  if (session.value && session.value.items && session.value.items[itemIdx.value]) {
    const firstItemVariants = session.value.items[itemIdx.value].variants || {};

    for (const key in firstItemVariants) {
      const variant = firstItemVariants[key];
      if (!variant?.idx) continue;

      const parts = variant.idx.split('.');

      if (parts[0] != "stones" || parts.length != 3) continue
      if (parts[2] == "add" || parts[2] == "remove") continue

      const groupIdx = parseInt(parts[1].toString())
      const groupIdxStr = "IDX" + groupIdx

      if (typeof groups[groupIdxStr] == "undefined") {
        groups[groupIdxStr] = {
          "group_idx": groupIdx,
          "stone_layout": "",
          "stone_type": "",
          "stone_quality": "",
          "stone_size": "",
          "key": {}
        }
      }

      groups[groupIdxStr][parts[2]] = variant["text"]
      groups[groupIdxStr].stone_layout = variant["stone_layout"]
      groups[groupIdxStr]["key"][parts[2]] = variant["key"] || ""

    }
  }

  return groups
})

const stoneGroupList = computed(() => Object.values(stoneGroups.value))

const activeGroupIdx = ref(-1)

/**
 * stone group layout (which of the group-detail components is shown)
 *
 * Set via the stoneLayoutTypes list rendered above the stone group details: "single",
 * "row" and "crown" pick the matching component, "noStones" removes the current group
 * instead - mirroring handleStoneLayoutClick.
 */

const activeGroupLayout = computed(() => activeGroupIdx.value !== -1 ? stoneGroups.value["IDX" + activeGroupIdx.value]?.stone_layout : null)

// Set to true right before activeGroupIdx is changed programmatically (see "open newly
// added stone group" below), so the watcher right after doesn't reset the layout that
// was just chosen for that same navigation.
let skipActiveGroupLayoutReset = false

watch(activeGroupIdx, () => {
  if (skipActiveGroupLayoutReset) {
    skipActiveGroupLayoutReset = false
    return
  }
})

/**
 * open newly added stone group
 *
 * handleStoneLayoutClick sets pendingNewGroupLayout before sending the "stones.add"
 * patch - the group itself only exists once the session has reloaded with it, so this
 * watches stoneGroups (fed by itemV3StonesList.vue's "groups" event) for the group_idx
 * that wasn't there before and opens it directly in the matching layout view.
 */

const pendingNewGroupLayout = ref<string | null>(null)
let previousStoneGroupIdxs: number[] = []

watch(stoneGroups, (newGroups) => {
  const newGroupIdxs = Object.values(newGroups).map((group: any) => group.group_idx)

  // Only jump into the first group when a new group was actually added. Any other patch
  // (e.g. changing a stone parameter within group 2) keeps the currently active group.
  if (pendingNewGroupLayout.value !== null && newGroupIdxs.length !== previousStoneGroupIdxs.length) {
    if (newGroupIdxs.length > previousStoneGroupIdxs.length) {
      activeGroupIdx.value = 0
    } else {
      activeGroupIdx.value = -1
    }
    pendingNewGroupLayout.value = null
  }


  previousStoneGroupIdxs = newGroupIdxs
})

const stoneGroupTabIdx = computed(() => {
  if (activeGroupIdx.value === -1) return 0
  const idx = stoneGroupList.value.findIndex((group: any) => group.group_idx === activeGroupIdx.value)
  return idx === -1 ? 0 : idx + 1
})

/**
 * stone group slider
 *
 * The tab buttons size themselves to their own text (min-w-max) instead of being
 * forced into equal percentage-wide slots, so long group titles never overflow. That
 * means the sliding highlight can no longer be positioned with a simple percentage
 * calculation - it has to measure the actual active button's position/width in the DOM.
 */

const stoneGroupTabRefs = ref<(HTMLElement | null)[]>([])

const setStoneGroupTabRef = (el: any, idx: number) => {
  stoneGroupTabRefs.value[idx] = el
}

const stoneGroupSliderRect = ref({left: 0, width: 0})

const updateStoneGroupSliderRect = () => {
  const button = stoneGroupTabRefs.value[stoneGroupTabIdx.value]
  if (button) {
    stoneGroupSliderRect.value = {left: button.offsetLeft + 2, width: button.offsetWidth - 4}
  }
}

watch([stoneGroupTabIdx, stoneGroups], () => nextTick(updateStoneGroupSliderRect), {immediate: true})

const stoneGroupSliderStyle = computed(() => ({
  width: `${stoneGroupSliderRect.value.width}px`,
  left: `${stoneGroupSliderRect.value.left}px`,
}))

/**
 * Patch
 */

const handleValuePatch = (actionIdx: string, value: any) => {
  const patchValue = (typeof value === "string") ? value : value.idx

  if (actionIdx === "stones.add" && hasMultipleItems.value && patchMultiple) {
    let targetItemIdxs = [itemIdx.value]

    if (configureAll.value) {
      targetItemIdxs = applyToBothRings.value
        ? session.value.items.map((_: any, idx: number) => idx)
        : [0]
    }

    const items = targetItemIdxs.map((idx: number) => ({
      itemIdx: idx,
      action: actionIdx,
      value: patchValue
    }));

    patchMultiple(items);
    return;
  }

  emit('patch', {
    action: actionIdx,
    value: patchValue,
    certificated_diamond: typeof value === "string" ? undefined : value.certificated_diamond
  });
};

/**
 * tip texts
 */

const hoverLayoutType = ref<string | null>(null)

const getTipTitle = computed(() => {
  const key = hoverLayoutType.value || "common"
  return t('components.parameters.tips.stones.' + key + '.title')
})

const getTipText = computed(() => {
  const key = hoverLayoutType.value || "common"
  return t('components.parameters.tips.stones.' + key + '.text')
})

const getStoneLayoutTipImageUrl = (layoutType: string, size: 300 | 600 = 600) => {
  return `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/tips/stone_layout/${size}/${layoutType}.webp`
}

const getTipImageUrl = computed(() => {
  if (!hoverLayoutType.value) return getStoneLayoutTipImageUrl("common")
  return getStoneLayoutTipImageUrl(hoverLayoutType.value)
})


</script>

<template>
  <item-v3-parameter-box
      v-model:open="open"
      :image-width-percent=imageWidthPercent
      :image=imageUrl
      :header-image="getHeaderImage()"
      :header-image-srcset="getHeaderImage() + ' 300w, ' + getHeaderImage('1200x600') +' 600w'"
      title="Steinbesatz"
      :subtitle="currentStonesText || 'Wählen Sie die Steinbesatz für Ihre Trauringe'"
      :loading=loading
      :tip-title="getTipTitle"
      :tip-text="getTipText"
      :tip-image="getTipImageUrl"
  >
    <template v-slot:main>
      <item-v3-stones-list
          v-if="activeGroupIdx === -1"
          v-model:active-group-idx="activeGroupIdx"
          :stoneGroups="stoneGroups"
          @patch="(event) => emit('patch', event)"
      />
      <div v-if="activeGroupIdx === -1 && hasMultipleItems && configureAll" class="flex flex-col gap-y-2 mt-4 w-full justify-between">
        <nav class="w-full">
          <!-- rings target selection -->
          <div
              class="relative flex items-center w-full h-[22px] max-h-[22px] bg-[#f3f0e9] rounded-full p-[2px] border border-[#e8e2d8] select-none">
            <!-- Sliding Background -->
            <div
                class="absolute top-[2px] bottom-[2px] bg-brand-main rounded-full transition-all duration-300 ease-in-out shadow-xs pointer-events-none"
                :style="ringsSliderStyle"
            ></div>

            <!-- Ring target Buttons -->
            <button
                type="button"
                class=" text-[10px] tracking-wided relative flex-1 flex cursor-pointer items-center justify-center text-xs transition-colors duration-200 z-10 font-normal leading-none h-full"
                :class="!applyToBothRings ? 'text-white font-medium' : 'text-gray-500 hover:text-gray-700'"
                @click="applyToBothRings = false"
            >
              Nur Ring 1
            </button>
            <button
                type="button"
                class=" text-[10px] tracking-wided relative flex-1 flex cursor-pointer items-center justify-center text-xs transition-colors duration-200 z-10 font-normal leading-none h-full"
                :class="applyToBothRings ? 'text-white font-medium' : 'text-gray-500 hover:text-gray-700'"
                @click="applyToBothRings = true"
            >
              Beide Ringe
            </button>
          </div>
        </nav>
      </div>
      <!-- no stones / add stones horizontal layout list -->
      <ul v-if="activeGroupIdx === -1" class="flex gap-x-1 overflow-x-auto no-scrollbar mt-4 w-full justify-between">
        <li
            v-for="layoutType in stoneLayoutTypes"
            :key="layoutType"
            class="shrink-0 flex-1 cursor-pointer flex flex-col items-center gap-1 py-2 px-1 rounded-2xl border-2 border-transparent bg-white hover:bg-[#f3f0e9]"
            @click="handleStoneLayoutClick(layoutType)"
            @mouseover="hoverLayoutType = layoutType"
            @mouseleave="hoverLayoutType = null"
        >
          <div class="w-full aspect-square rounded-2xl flex overflow-hidden bg-[#f8f5ee]">
            <img
                :src="getStoneLayoutImageUrl(layoutType, 300)"
                :srcset="getStoneLayoutImageUrl(layoutType, 600)"
                class="w-full h-full object-cover" alt=""/>
          </div>
          <span class="text-parameter-title text-[10px] font-medium overflow-hidden mx-auto text-center mt-1">
            {{ getStoneLayoutLabel(layoutType) }}
          </span>
          <span v-if="getStoneLayoutSubtitle(layoutType)"
                class="text-parameter-text text-[9px] overflow-hidden mx-auto text-center">
            {{ getStoneLayoutSubtitle(layoutType) }}
          </span>
        </li>
      </ul>
      <div v-if="activeGroupIdx !== -1" class="w-full mt-4">
        <!-- stone group list switch-button bar -->
        <nav class="w-full mb-4">
          <div
              class="relative flex items-center w-full h-[22px] max-h-[22px] overflow-x-auto no-scrollbar bg-[#f3f0e9] rounded-full p-[2px] border border-[#e8e2d8] select-none">
            <!-- Sliding Background -->
            <div
                class="absolute top-[2px] bottom-[2px] bg-brand-main rounded-full transition-all duration-300 ease-in-out shadow-xs pointer-events-none"
                :style="stoneGroupSliderStyle"
            ></div>

            <button
                :ref="(el) => setStoneGroupTabRef(el, 0)"
                type="button"
                class="text-[10px] tracking-wided text-nowrap relative flex-1 min-w-max flex cursor-pointer items-center justify-center px-3 text-xs transition-colors duration-200 z-10 font-normal leading-none h-full"
                :class="activeGroupIdx === -1 ? 'text-white font-medium' : 'text-gray-500 hover:text-gray-700'"
                @click="activeGroupIdx = -1"
            >
              Übersicht
            </button>
            <button
                v-for="(group, index) in stoneGroupList"
                :key="group.group_idx"
                :ref="(el) => setStoneGroupTabRef(el, index + 1)"
                type="button"
                class="text-[10px] tracking-wided text-nowrap relative flex-1 min-w-max flex cursor-pointer items-center justify-center px-3 text-xs transition-colors duration-200 z-10 font-normal leading-none h-full"
                :class="activeGroupIdx === group.group_idx ? 'text-white font-medium' : 'text-gray-500 hover:text-gray-700'"
                @click="activeGroupIdx = group.group_idx"
            >
              {{ getStoneGroupTitle(group) }}
            </button>
          </div>
        </nav>
        <!-- no stones / add stones horizontal layout list -->
        <ul class="flex gap-x-1 overflow-x-auto no-scrollbar mb-4 w-full justify-between">
          <li
              v-for="layoutType in stoneLayoutTypes"
              :key="layoutType"
              class="shrink-0 flex-1 cursor-pointer flex flex-col items-center gap-1 py-2 px-1 rounded-2xl border-2 bg-white hover:bg-[#f3f0e9]"
              :class="activeGroupLayout === layoutType ? 'border-brand-main bg-[#f3f0e9]' : 'border-transparent'"
              @click="handleGroupLayoutClick(layoutType)"
              @mouseover="hoverLayoutType = layoutType"
              @mouseleave="hoverLayoutType = null"
          >
            <div class="w-full aspect-square rounded-2xl flex overflow-hidden bg-[#f8f5ee]">
              <img
                  :src="getStoneLayoutImageUrl(layoutType, 300)"
                  :srcset="getStoneLayoutImageUrl(layoutType, 600)"
                  class="w-full h-full object-cover" alt=""/>
            </div>
            <span class="text-parameter-title text-[10px] font-medium overflow-hidden mx-auto text-center mt-1">
              {{ getStoneLayoutLabel(layoutType) }}
            </span>
            <span v-if="getStoneLayoutSubtitle(layoutType)"
                  class="text-parameter-text text-[9px] overflow-hidden mx-auto text-center">
              {{ getStoneLayoutSubtitle(layoutType) }}
            </span>
          </li>
        </ul>

      </div>

    </template>
    <template v-slot:details>
      <!-- stone group details -->
      <item-v3-stones-group-single
          v-if="activeGroupLayout === 'single'"
          :group-idx="activeGroupIdx"
          @patch="(event) => emit('patch', event)"
      />
      <item-v3-stones-group-rows
          v-else-if="activeGroupLayout === 'row'"
          :group-idx="activeGroupIdx"
          @patch="(event) => emit('patch', event)"
      />
      <item-v3-stones-group-crown
          v-else-if="activeGroupLayout === 'crown'"
          :group-idx="activeGroupIdx"
          @patch="(event) => emit('patch', event)"
      />
      <item-v3-stones-group-starry-sky
          v-else-if="activeGroupLayout === 'starry_sky'"
          :group-idx="activeGroupIdx"
          @patch="(event) => emit('patch', event)"
      />
    </template>
  </item-v3-parameter-box>

  <!-- Remove Stones / Stone Group Confirmation Modal -->
  <item-v3-remove-group-modal v-model="showRemoveModal" :item-name="pendingRemoveItemName"
                              @confirm="handleRemoveStones"/>
</template>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
  display: none;
}

.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>